import React, { useMemo } from 'react';

import amplitude from 'amplitude-js';
import { Button } from 'apps/auth/components/common/Button/Button';
import { DiskBadge } from 'apps/auth/components/common/DiskBadge/DiskBadge';
import { Icon } from 'apps/auth/components/common/Icon/Icon';
import { Row } from 'apps/auth/components/common/Layout/Row';
import { Text } from 'apps/auth/components/common/Text/Text';
import { TRANSFER_STATUS_DETAILS_CLICKED } from 'apps/auth/constants/amplitude/AmplitudeEvents';
import { TRANSFER_STATUS_SCREEN_MODAL } from 'apps/auth/constants/ComponentNames';
import {
    CATEGORY_TRANSFER_TO_ALIPAY,
    CATEGORY_TRANSFER_TO_GLOBAL_ACCOUNT,
    CATEGORY_TRANSFER_TO_LIBRE,
    CATEGORY_TRANSFER_TO_WECHAT,
    STATUS_OK,
} from 'apps/auth/constants/Constants';
import { URL_IMAGES_BANKS } from 'apps/auth/constants/UrlConstants';
import { USA } from 'apps/auth/constants/WellKnownCountries';
import {
    getIconForDiskBadge,
    getSendHistoryIcon,
    getSpecialStatus,
    getStatusText,
    getTextColorFromStatus,
    isShowHowToPay,
} from 'apps/auth/helpers/historyUtils';
import { resolveArguments, toLowerCase } from 'apps/auth/helpers/utils';
import { useAuth, useCheck, useCheckMeta, useChequeExtra, usePageMessages } from 'apps/auth/hooks/selectors';
import { useThemeDetector } from 'apps/auth/hooks/theme';
import { MarkdownService } from 'apps/auth/services/MarkdownService';

const CheckHeader = (props) => {
    const { chequeUsaNotReceipt, cancelTransactionInfo, blockToRecipient, viewStatusDetailsButton, draftChequeStatus, draftChequeInfo, ...messages } =
        usePageMessages();
    const { countryId: userCountryId } = useAuth();
    const cheque = useCheck();
    const extra = useChequeExtra();
    const { canCancelTransaction } = useCheckMeta();
    const { theme } = useThemeDetector();

    const lastStatus = useMemo(() => {
        let index = -1;
        if (extra.paymentStatusHistory) {
            for (let i = extra.paymentStatusHistory.length - 1; i >= 0; i--) {
                if (extra.paymentStatusHistory?.[i]?.date) {
                    index = i;
                    break;
                }
            }
        }
        return index === -1 ? null : extra.paymentStatusHistory[index];
    }, [extra.paymentStatusHistory]);

    const isDraftOrFraud = cheque.draft || cheque.draftFraud;
    const statusDescription = isDraftOrFraud && !cheque.extra.draftCompletedVerification ? draftChequeInfo : getSpecialStatus(cheque, messages, lastStatus);

    const UsaWithoutLibre = userCountryId === USA && cheque.providerToCategory !== CATEGORY_TRANSFER_TO_LIBRE;
    const withoutIconName =
        (cheque.toBankAlias && cheque.providerToCategory !== CATEGORY_TRANSFER_TO_LIBRE) ||
        cheque.providerToCategory === CATEGORY_TRANSFER_TO_WECHAT ||
        cheque.providerToCategory === CATEGORY_TRANSFER_TO_ALIPAY;
    const isDisabled = (cheque.status > STATUS_OK && !cheque.draft) || cheque.extra?.draftOnfidoTooManyAttempts;

    const getTimerValue = () => (props.cancelTimer >= 60 ? (props.cancelTimer - (props.cancelTimer % 60)) / 60 : props.cancelTimer);

    const cancelTransactionTimer = () => {
        const regex = /(<([^>]+)>)/gi;
        const text = cancelTransactionInfo.replace(regex, '').slice(0, -1);
        return (
            <Row flex align="center" indent="adaptive-32-48">
                {text} {getTimerValue()} {props.timerUnit}.
            </Row>
        );
    };

    const getAvatar = (chequeForAvatar) => {
        if (chequeForAvatar.providerToCategory === CATEGORY_TRANSFER_TO_LIBRE) {
            return null;
        }
        if (chequeForAvatar.providerToCategory === CATEGORY_TRANSFER_TO_WECHAT) {
            return `${URL_IMAGES_BANKS}/wechat.png`;
        } else if (chequeForAvatar.providerToCategory === CATEGORY_TRANSFER_TO_ALIPAY) {
            return `${URL_IMAGES_BANKS}/alipay-big.png`;
        } else if (chequeForAvatar.toBankAlias) {
            return `${URL_IMAGES_BANKS}/${chequeForAvatar.toBankAlias}.png`;
        }
        return null;
    };

    const getAmount = (chequeForAmount) => {
        const isDraftOrFraud = chequeForAmount.draft || chequeForAmount.draftFraud;
        const fromCurrency =
            chequeForAmount.amountFromCurrency && typeof chequeForAmount.amountFromCurrency === 'string'
                ? chequeForAmount.amountFromCurrency.toLowerCase()
                : '';
        const toCurrency =
            chequeForAmount.amountToCurrency && typeof chequeForAmount.amountToCurrency === 'string' ? chequeForAmount.amountToCurrency.toLowerCase() : '';
        return (
            <>
                {chequeForAmount.providerToCategory === CATEGORY_TRANSFER_TO_GLOBAL_ACCOUNT && (
                    <>
                        <Text size="header" color={getTextColorFromStatus(isDisabled)} ellipsis lineThrough={isDisabled}>
                            {`${chequeForAmount.amountTo} `}
                            <Text size="header" smallCaps color={getTextColorFromStatus(isDisabled)}>
                                {toCurrency}
                            </Text>
                        </Text>
                    </>
                )}
                {chequeForAmount.providerToCategory !== CATEGORY_TRANSFER_TO_GLOBAL_ACCOUNT && (
                    <>
                        {!isDraftOrFraud && (
                            <>
                                <Text size="header" color={getTextColorFromStatus(isDisabled)} ellipsis lineThrough={isDisabled}>
                                    {`${chequeForAmount.amountFrom} `}
                                    <Text size="header" smallCaps color={getTextColorFromStatus(isDisabled)}>
                                        {fromCurrency}
                                    </Text>
                                </Text>
                                <Text size="header" color={getTextColorFromStatus(isDisabled)}>
                                    {' → '}
                                </Text>
                            </>
                        )}
                        <Text size="header" color={getTextColorFromStatus(isDisabled)} ellipsis lineThrough={isDisabled}>
                            {`${chequeForAmount.amountTo} `}
                            <Text size="header" smallCaps color={getTextColorFromStatus(isDisabled)}>
                                {toCurrency}
                            </Text>
                        </Text>
                    </>
                )}
            </>
        );
    };

    const getStatus = () => {
        props.setModal(TRANSFER_STATUS_SCREEN_MODAL);
    };

    const sendAmplitudeEvent = () => {
        amplitude.getInstance().logEvent(TRANSFER_STATUS_DETAILS_CLICKED);
    };

    const onIconRenderError = (e) => {
        const defaultIcon = `${URL_IMAGES_BANKS}/card-80${theme === 'dark' ? '-dark' : ''}.svg`;
        e.target.onerror = null;
        e.target.src = defaultIcon;
    };

    return (
        <>
            {UsaWithoutLibre && (
                <Text variant="div" size="secondary" indent="adaptive-24-32">
                    {chequeUsaNotReceipt}
                </Text>
            )}
            <Row indent="adaptive-16-24">
                <DiskBadge
                    iconName={withoutIconName ? null : getIconForDiskBadge(cheque, cheque.providerToCategory, cheque.transactionType) + `-48`}
                    src={getAvatar(cheque)}
                    diskSize="large"
                    isDisabled={isDisabled}
                    flagName={toLowerCase(cheque.toBankCountryCode)}
                    onError={onIconRenderError}
                />
            </Row>
            <Text variant="div" color={getTextColorFromStatus(isDisabled)} indent="4px" ellipsis>
                {resolveArguments(blockToRecipient, cheque.toProviderName)}
            </Text>
            <Row indent="adaptive-48-64">
                <Row indent="adaptive-32-48">
                    <Row indent="adaptive-8-16">{getAmount(cheque)}</Row>
                    {cheque.comment && (
                        <Text variant="div" color={getTextColorFromStatus(cheque)}>
                            {cheque.comment}
                        </Text>
                    )}
                </Row>
                <Row flex align="center" indent={canCancelTransaction || statusDescription ? 'adaptive-32-48' : null}>
                    <Icon name={getSendHistoryIcon(cheque)} rightIndent="16px" />
                    {isDraftOrFraud && draftChequeStatus ? draftChequeStatus : getStatusText(cheque, messages, lastStatus)}
                </Row>
                {canCancelTransaction && props.cancelTimer !== 0 && !isShowHowToPay(cheque) && cancelTransactionTimer()}
                {MarkdownService.splitByParagraph(MarkdownService.replaceBrWithNewlines(statusDescription || ''))}
                <Button
                    type="button"
                    size="tight"
                    variant="row"
                    caption={viewStatusDetailsButton}
                    negativeMargin
                    onClick={() => {
                        sendAmplitudeEvent();
                        getStatus();
                    }}
                />
            </Row>
        </>
    );
};
export default CheckHeader;
